import { injectable, Container, inject } from "../injection";
import { MessageBusClient } from "./MessageBusClient";
import { locationPrepend } from "../devServer/locationPrepend";

interface ISend<R> {
    request: RedDeer.Contracts.Base.Message;
    description: string;
    callback: (response: R) => void;
    error?: (error: Error) => void;
    timeoutInMs?: number;
}

type IPromiseSend = Omit<ISend<unknown>, "callback" | "error">;

interface IAjaxSend extends IPromiseSend {
    signal?: AbortSignal;
}

interface ISubscription<R> {
    request: RedDeer.Contracts.Base.Message;
    description: string;
    callback: (response: R) => void;
}

@injectable("singleton")
export class MessageBusService {
    /** passing Container is bad pracitse. but it is needed here to avoid a circular dependency with MessageBusClient and LegacyUserDetailsStore */
    constructor(@inject(Container) private container: Container) {}

    public getConnectedClient(): MessageBusClient {
        return MessageBusClient.getConnectedInstance(this.container);
    }

    public getClient(): MessageBusClient {
        return MessageBusClient.getInstance(this.container);
    }

    public send<R>(config: ISend<R>): void {
        this.getConnectedClient().send(
            config.request, // message
            config.description, // currentOperation
            config.callback, // responseCallback
            undefined, // finallyOnce
            config.error, // errorCallback,
            config.timeoutInMs // timeoutInMs
        );
    }

    public sendPromise<R>(config: IPromiseSend): Promise<R> {
        return new Promise((resolve, reject) => {
            this.send({
                ...config,
                callback: resolve,
                error: reject
            });
        });
    }

    public asyncSend<R>(config: ISend<R>): void {
        this.getConnectedClient().asyncSend(
            config.request, // message
            config.description, // currentOperation
            config.callback, // responseCallback
            undefined, // finallyOnce
            config.error, // errorCallback,
            config.timeoutInMs // timeoutInMs
        );
    }

    public asyncSendPromise<R>(config: IPromiseSend): Promise<R> {
        return new Promise((resolve, reject) => {
            this.asyncSend({
                ...config,
                callback: resolve,
                error: reject
            });
        });
    }

    public asyncSendAjax<R>(config: IAjaxSend): Promise<R> {
        return this.sendAjaxInternal<R>("AsyncSendAjax", config);
    }

    public sendAjax<R>(config: IAjaxSend): Promise<R> {
        return this.sendAjaxInternal<R>("SendAjax", config);
    }

    private async sendAjaxInternal<R>(
        endpoint: string,
        config: IAjaxSend
    ): Promise<R> {
        const url = `${locationPrepend()}/MessageBus/${endpoint}`;

        // Define request options
        const requestOptions: RequestInit = {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(config.request),
            credentials: "include",
            signal: config.signal
        };

        // Make the POST request using fetch
        const response = await fetch(url, requestOptions);

        // Check if the request was successful (status code 200)
        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        // Parse the JSON response
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const data = await response.json();

        // Resolve with successful response data
        return data as R;
    }

    public subscribe<R>(config: ISubscription<R>): () => void {
        return this.getConnectedClient().subscribe(
            config.request, // message
            config.description, // currentOperation
            config.callback as (
                response: RedDeer.Contracts.Base.CallbackResponse
            ) => void // responseCallback
        );
    }

    public asyncSubscribe<R>(config: ISubscription<R>): () => void {
        return this.getConnectedClient().asyncSubscribe(
            config.request, // message
            config.description, // currentOperation
            config.callback as (
                response: RedDeer.Contracts.Base.CallbackResponse
            ) => void // responseCallback
        );
    }
}
